import { z } from 'zod';
import {
    ResourceLibraryNewProductIntroductionBaseItem,
    ResourceLibraryNewProductIntroductionItem,
} from '../types/resourceLibraryItems';
import {
    GetResourceLibraryNewProductIntroductionItemSchema,
    ResourceLibraryNewProductIntroductionBaseItemSchema,
    ResourceLibraryNewProductIntroductionItemSchema,
} from '../schemas/resourceLibraryNewProductIntroductionItemZodSchemas';
import {
    ProductGroupSchema,
    ProductApplicationSchema,
    NavigationNodeSchema,
} from '../schemas/resourceLibraryBaseZodSchemas';
import sanitizeRoute from '../../../common/tools/sanitizeRoute';
import {
    normalizeSingleLine,
    parseBodyToParagraphs,
    splitPlaintextToList,
} from '../../../common/tools/transformUtils';

export function hydrateResourceLibraryNewProductIntroductionBaseItem(
    item: z.infer<typeof ResourceLibraryNewProductIntroductionBaseItemSchema>
): ResourceLibraryNewProductIntroductionBaseItem {
    return {
        kind: 'newProductIntroduction',
        id: item.id,
        key: item.key,
        name: item.name,
        description: item.description ?? '',
        brandId: item.brand ? item.brand.id : null,
        productGroupIds: item.productGroup
            ? item.productGroup.map((pg: z.infer<typeof ProductGroupSchema>) => pg.id)
            : [],
        productApplicationIds: item.productApplication
            ? item.productApplication.map((pa: z.infer<typeof ProductApplicationSchema>) => pa.id)
            : [],
        resourceTypeId: item.resourceType.id,
        navigationScopeIds: item.navigationScope
            ? item.navigationScope.map((ns: z.infer<typeof NavigationNodeSchema>) => ns.id)
            : [],
        exclusiveNavigationScopeIds: item.exclusiveNavigationScope
            ? item.exclusiveNavigationScope.map(
                  (ens: z.infer<typeof NavigationNodeSchema>) => ens.id
              )
            : [],
        publicationDate: item.publicationDate ?? null,
        thumbnailUrl: item.thumbnailUrl,
        coverImageUrl: item.coverImage.fullpath,
    };
}

export function hydrateResourceLibraryNewProductIntroductionItem(
    item: z.infer<typeof ResourceLibraryNewProductIntroductionItemSchema>
): ResourceLibraryNewProductIntroductionItem {
    const base = hydrateResourceLibraryNewProductIntroductionBaseItem(item);
    return {
        ...base,
        headline: normalizeSingleLine(item.headline),
        body: item.body,
        bodyParagraphs: parseBodyToParagraphs(item.body),
        quickFacts: splitPlaintextToList(item.quickFacts),
        features: splitPlaintextToList(item.features),
        customerValues: splitPlaintextToList(item.customerValues),
        targetApplications: splitPlaintextToList(item.targetApplications),
        closingSummary: normalizeSingleLine(item.closingSummary),
        marketAdvantages: splitPlaintextToList(item.marketAdvantages),
        productBrief: {
            id: item.productBrief.id,
            kind:
                item.productBrief.__typename === 'object_ResourceLibraryArticleItem'
                    ? 'article'
                    : 'asset',
        },
        similarProductsRoute: sanitizeRoute(item.similarProductsRoute),
    };
}

export function transformResourceLibraryNewProductIntroductionItemResponse(
    data: unknown
): ResourceLibraryNewProductIntroductionItem | null {
    const parsedResult = GetResourceLibraryNewProductIntroductionItemSchema.safeParse(data);

    if (!parsedResult.success) {
        console.error(
            'Validation failed for getResourceLibraryNewProductIntroductionItem:',
            parsedResult.error.errors
        );
        return null;
    }

    const item = parsedResult.data.getResourceLibraryNewProductIntroductionItem;
    return hydrateResourceLibraryNewProductIntroductionItem(item);
}
